import React from 'react';
import AdSenseAd from './AdSenseAd';
import { useGoogleAdSense } from '@/hooks/useGoogleAdSense';

interface AdSenseComponentProps {
  slot?: string;
  className?: string;
  style?: React.CSSProperties;
}

// Leaderboard Ad (970x90) - Common for top of pages
export const AdSenseLeaderboard: React.FC<AdSenseComponentProps> = ({ 
  slot, 
  className = '',
  style 
}) => {
  const { adSlots } = useGoogleAdSense();
  const adSlot = slot || adSlots.leaderboard || '9589588510'; // fallback
  
  return (
    <AdSenseAd
      slot={adSlot}
      format="auto"
      responsive={true}
      className={className}
      style={{
        minWidth: '320px',
        minHeight: '90px',
        width: '100%',
        maxWidth: '970px',
        ...style
      }}
    />
  );
};

// Banner Ad (728x90) - Standard banner
export const AdSenseBanner: React.FC<AdSenseComponentProps> = ({ 
  slot, 
  className = '',
  style 
}) => {
  const { adSlots } = useGoogleAdSense();
  const adSlot = slot || adSlots.banner || '9589588510'; // fallback
  
  return (
    <AdSenseAd
      slot={adSlot}
      format="auto"
      responsive={true}
      className={className}
      style={{
        minWidth: '320px',
        minHeight: '90px',
        width: '100%',
        maxWidth: '728px',
        ...style
      }}
    />
  );
};

// Rectangle Ad (336x280) - Medium rectangle
export const AdSenseRectangle: React.FC<AdSenseComponentProps> = ({ 
  slot, 
  className = '',
  style 
}) => {
  const { adSlots } = useGoogleAdSense();
  const adSlot = slot || adSlots.rectangle || '9589588510'; // fallback
  
  return (
    <AdSenseAd
      slot={adSlot}
      format="rectangle"
      responsive={true}
      className={className}
      style={{
        minWidth: '300px',
        minHeight: '250px',
        width: '336px',
        height: '280px',
        ...style
      }}
    />
  );
};

// Large Rectangle Ad (300x250) - Most common
export const AdSenseLargeRectangle: React.FC<AdSenseComponentProps> = ({ 
  slot, 
  className = '',
  style 
}) => {
  const { adSlots } = useGoogleAdSense();
  const adSlot = slot || adSlots.rectangle || '9589588510'; // fallback
  
  return (
    <AdSenseAd
      slot={adSlot}
      format="auto"
      responsive={true}
      className={className}
      style={{
        minWidth: '300px',
        minHeight: '250px',
        width: '100%',
        maxWidth: '300px',
        ...style
      }}
    />
  );
};

// Responsive Ad - Adapts to container
export const AdSenseResponsive: React.FC<AdSenseComponentProps> = ({ 
  slot, 
  className = '',
  style 
}) => {
  const { adSlots } = useGoogleAdSense();
  const adSlot = slot || adSlots.responsive || '9589588510'; // fallback
  
  return (
    <AdSenseAd
      slot={adSlot}
      format="auto"
      responsive={true}
      className={className}
      style={{
        display: 'block',
        width: '100%',
        minHeight: '250px',
        ...style
      }}
    />
  );
};

// Mobile Banner (320x50) - For mobile devices
export const AdSenseMobileBanner: React.FC<AdSenseComponentProps> = ({ 
  slot, 
  className = '',
  style 
}) => {
  const { adSlots } = useGoogleAdSense();
  const adSlot = slot || adSlots.mobile || '9589588510'; // fallback
  
  return (
    <AdSenseAd
      slot={adSlot}
      format="auto"
      responsive={true}
      className={`${className} block sm:hidden`.trim()}
      style={{
        minWidth: '320px',
        minHeight: '50px',
        width: '100%',
        maxWidth: '320px',
        ...style
      }}
    />
  );
};

// In-feed Ad - For content feeds
export const AdSenseInFeed: React.FC<AdSenseComponentProps & { layoutKey?: string }> = ({ 
  slot, 
  className = '',
  style,
  layoutKey = '-fg+f-h-50+aq'
}) => {
  const { adSlots } = useGoogleAdSense();
  const adSlot = slot || adSlots.inFeed || '9589588510'; // fallback
  
  return (
    <AdSenseAd
      slot={adSlot}
      format="fluid"
      responsive={true}
      layoutKey={layoutKey}
      className={className}
      style={{
        display: 'block',
        width: '100%',
        minHeight: '250px',
        ...style
      }}
    />
  );
};

// Container component for centering ads - Hides when AdSense is not enabled
export const AdSenseContainer: React.FC<{
  children: React.ReactNode;
  className?: string;
  maxWidth?: string;
}> = ({ 
  children, 
  className = '',
  maxWidth = '970px' 
}) => {
  const { isAdSenseEnabled } = useGoogleAdSense();
  
  // Don't render anything if AdSense is not enabled/approved
  if (!isAdSenseEnabled) {
    return null;
  }
  
  return (
    <div className={`flex justify-center ${className}`.trim()}>
      <div 
        style={{ 
          width: '100%', 
          maxWidth,
          minWidth: '300px' 
        }}
      >
        {children}
      </div>
    </div>
  );
}; 